import React, { useCallback, useEffect, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";

import { SignInForm } from "src/domains/auth/components/SignInForm";
import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import type { Organization } from "src/domains/organization/types";
import { ORGANIZATIONS } from "src/platform/constants/routes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import { Layout } from "./Layout";

/** Minimal shape of GET /api/v1/organizations response we consume. */
interface OrganizationListPayload {
  organizations: Organization[];
  total?: number;
}

type LoginView = "form" | "org-select" | "loading";

const Login: React.FC = () => {
  const { user, loading } = useLegacyAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [view, setView] = useState<LoginView>("form");
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [selectingAlias, setSelectingAlias] = useState<string | null>(null);

  // After a successful login the auth context populates `user`. We then fetch
  // the user's organizations to drive org selection (the step-2 session is
  // established by selecting an org here).
  const loadOrgs = useCallback(async () => {
    setView("loading");
    setOrgError(null);
    try {
      const response = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/organizations?limit=100`,
        {
          credentials: "include",
          headers: { Accept: "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to load organizations (HTTP ${response.status})`,
        );
      }

      const data = (await response.json()) as OrganizationListPayload;
      const list = data.organizations ?? [];

      if (list.length === 0) {
        // No orgs -> go create one
        void navigate(ORGANIZATIONS.CREATE.path, { replace: true });
        return;
      }

      if (list.length === 1) {
        // Single org -> auto-select and navigate to details
        const alias = list[0].alias;
        setSelectingAlias(alias);
        await orgSessionManager.selectOrg(alias);
        void navigate(ORGANIZATIONS.DETAILS.getPath(alias), {
          replace: true,
        });
        return;
      }

      // 2+ orgs -> show selection UI
      setOrgs(list);
      setView("org-select");
    } catch (err) {
      setOrgError(
        err instanceof Error ? err.message : "Failed to load organizations",
      );
      setView("org-select");
    }
  }, [navigate]);

  useEffect(() => {
    if (user && !loading && view === "form") {
      void loadOrgs();
    }
  }, [user, loading, view, loadOrgs]);

  const handleSelectOrg = useCallback(
    async (alias: string) => {
      setSelectingAlias(alias);
      setOrgError(null);
      try {
        await orgSessionManager.selectOrg(alias);
        void navigate(ORGANIZATIONS.DETAILS.getPath(alias), {
          replace: true,
        });
      } catch (err) {
        setSelectingAlias(null);
        setOrgError(
          err instanceof Error
            ? err.message
            : "Failed to select organization. Please try again.",
        );
      }
    },
    [navigate],
  );

  const handleBackToSignIn = useCallback(() => {
    setView("form");
    setOrgs([]);
    setOrgError(null);
  }, []);

  // Initial loading / redirecting state
  if (loading || user) {
    if (view === "form") {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {loading ? "Checking authentication..." : "Redirecting..."}
          </Typography>
        </Box>
      );
    }
  }

  // Org selection view (2+ orgs)
  if (view === "org-select") {
    return (
      <Layout
        Component={() => (
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <Typography
              fontWeight={700}
              sx={{ color: "text.primary", mb: 1, fontSize: "1.25rem" }}
            >
              Choose an organization
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2.5 }}
            >
              Rusha uses your active membership to scope access.
            </Typography>

            {orgError && (
              <Box
                role="alert"
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  color: theme.palette.error.main,
                  fontSize: "13px",
                }}
              >
                {orgError}
              </Box>
            )}

            <Box sx={{ display: "grid", gap: 1.5 }}>
              {orgs.map((org) => {
                const isSelected = selectingAlias === org.alias;
                return (
                  <Button
                    key={org.alias}
                    type="button"
                    fullWidth
                    disabled={selectingAlias !== null}
                    onClick={() => void handleSelectOrg(org.alias)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      p: 1.5,
                      textAlign: "left",
                      textTransform: "none",
                      bgcolor: isDark
                        ? theme.palette.grey[800]
                        : theme.palette.grey[50],
                      border: `1px solid ${
                        isDark
                          ? theme.palette.grey[700]
                          : theme.palette.grey[200]
                      }`,
                      borderRadius: "6px",
                      color: "text.primary",
                      fontSize: "12px",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        bgcolor: isDark
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.primary.main, 0.04),
                      },
                      "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: "2px",
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="span"
                        fontWeight={600}
                        sx={{ display: "block", color: "text.primary" }}
                      >
                        {org.alias}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          display: "block",
                          fontSize: "12px",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {org.description || org.name}
                      </Typography>
                    </Box>
                    {isSelected ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ArrowForwardIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                        aria-hidden="true"
                      />
                    )}
                  </Button>
                );
              })}
            </Box>

            <Button
              type="button"
              onClick={handleBackToSignIn}
              sx={{
                mt: 2,
                color: "primary.main",
                textTransform: "none",
                fontSize: "13px",
                "&:hover": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: "2px",
                },
              }}
            >
              ← Back to sign in
            </Button>
          </Box>
        )}
      />
    );
  }

  // Loading organizations view
  if (view === "loading") {
    return (
      <Layout
        Component={() => (
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              py: 4,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading your organizations...
            </Typography>
          </Box>
        )}
      />
    );
  }

  // Default login form
  return <Layout Component={SignInForm} />;
};

export default Login;
